import { useEffect, useState } from 'react';
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
  checkActionCode,
} from 'firebase/auth';
import { auth } from '../lib/firebase';

// Página que recebe os links dos e-mails do Firebase (redefinir senha, verificar
// e-mail etc.): https://flowaes.com/auth/action?mode=...&oobCode=...

const STRONG_PASSWORD = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]).{8,}$/;

// O continueUrl vem da própria URL do e-mail, então só aceitamos destinos
// internos (mesmo domínio). Qualquer outra coisa volta para "/".
export function safeContinueUrl(raw: string | null): string {
  if (!raw) return '/';
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) return '/';
    // Nunca volta para a própria página de ação (o código já foi usado)
    if (url.pathname.startsWith('/auth/action') || url.pathname.startsWith('/__/')) return '/';
    return url.pathname + url.search + url.hash;
  } catch {
    return '/';
  }
}

type Status = 'loading' | 'form' | 'done' | 'error';

export default function AuthActionPage() {
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode');
  const oobCode = params.get('oobCode') || '';
  const continueUrl = safeContinueUrl(params.get('continueUrl'));

  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (!oobCode) throw new Error('missing-code');
        switch (mode) {
          case 'resetPassword':
            // Valida o código e descobre de qual conta é
            setEmail(await verifyPasswordResetCode(auth, oobCode));
            if (!cancelled) setStatus('form');
            return;
          case 'verifyEmail':
            await applyActionCode(auth, oobCode);
            if (!cancelled) { setMessage('E-mail confirmado com sucesso!'); setStatus('done'); }
            return;
          case 'recoverEmail': {
            // Desfaz uma troca de e-mail não autorizada
            const info = await checkActionCode(auth, oobCode);
            await applyActionCode(auth, oobCode);
            if (!cancelled) {
              setMessage(`Seu e-mail foi restaurado para ${info.data.email}. Recomendamos redefinir a senha.`);
              setStatus('done');
            }
            return;
          }
          case 'verifyAndChangeEmail':
            await applyActionCode(auth, oobCode);
            if (!cancelled) { setMessage('Novo e-mail confirmado. Entre novamente com ele.'); setStatus('done'); }
            return;
          default:
            throw new Error('unsupported-mode');
        }
      } catch {
        if (!cancelled) {
          setMessage('Este link expirou, já foi usado ou é inválido. Solicite um novo.');
          setStatus('error');
        }
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  const handleReset = async () => {
    if (!STRONG_PASSWORD.test(password)) {
      setMessage('A senha deve ter no mínimo 8 caracteres, 1 letra maiúscula, 1 número e 1 símbolo.');
      return;
    }
    if (password !== confirm) {
      setMessage('As senhas não coincidem.');
      return;
    }
    setIsSaving(true);
    setMessage('');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setMessage('Senha alterada! Você já pode entrar com a nova senha.');
      setStatus('done');
    } catch {
      setMessage('Este link expirou ou já foi usado. Solicite um novo.');
      setStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass =
    'w-full rounded border border-teal-500/20 bg-[#0a0e27]/50 p-3 text-sm tracking-wider text-white focus:border-teal-400 focus:outline-none';

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-[#02040a] p-4 font-mono text-white overflow-y-auto">
      <div className="w-full max-w-sm space-y-5 rounded-xl border border-teal-500/20 bg-[#050714] p-6">
        <h1 className="text-center text-xl font-bold tracking-[6px] text-teal-400">FLOWAES</h1>

        {status === 'loading' && (
          <p className="text-center text-xs uppercase tracking-widest text-white/60">Validando link...</p>
        )}

        {status === 'form' && (
          <>
            <p className="break-all text-center text-[10px] uppercase tracking-wider text-white/50">
              Nova senha para {email}
            </p>
            <input
              type="password"
              placeholder="Nova senha"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
            <input
              type="password"
              placeholder="Confirmar senha"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputClass}
            />
            <p className="text-[9px] uppercase text-white/40">Mín. 8 caracteres, 1 maiúscula, 1 número, 1 símbolo.</p>
            {message && <p className="text-center text-xs text-red-400">{message}</p>}
            <button
              onClick={handleReset}
              disabled={isSaving}
              className="w-full rounded bg-teal-500 p-3 text-sm font-bold tracking-widest text-black transition hover:bg-teal-400 disabled:opacity-50"
            >
              {isSaving ? 'SALVANDO...' : 'SALVAR NOVA SENHA'}
            </button>
          </>
        )}

        {(status === 'done' || status === 'error') && (
          <>
            <p className={`text-center text-xs leading-relaxed ${status === 'done' ? 'text-teal-300' : 'text-red-400'}`}>
              {message}
            </p>
            <a
              href={continueUrl}
              className="block w-full rounded border border-teal-500/30 p-3 text-center text-xs font-bold tracking-widest text-teal-400 transition hover:bg-teal-500/10"
            >
              IR PARA O FLOWAES
            </a>
          </>
        )}
      </div>
    </div>
  );
}
